import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Loader,
  Segment,
  Icon,
  Label,
  Pagination,
  Input,
  Dropdown,
  Message,
  Menu,
  Table,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PAGE_SIZE = 30;

// DR-2026-236: the canonical controlled-doc type set (mirrors the endpoint's
// validated doc_type filter).
const DOC_TYPE_OPTIONS = [
  { key: '', value: '', text: 'All document types' },
  ...[
    'DR',
    'BUG',
    'CR',
    'ADR',
    'CAPA',
    'AIV',
    'PM',
    'QA',
    'SA',
    'SOP',
    'WI',
    'SPEC',
    'GUIDE',
  ].map((t) => ({ key: t, value: t, text: t })),
];

/** Group commits by calendar day (committed_at, local). */
function groupByDay(commits) {
  const groups = [];
  let current = null;
  commits.forEach((c) => {
    const d = new Date(c.committed_at);
    const key = isNaN(d.getTime())
      ? 'Unknown date'
      : d.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
    if (!current || current.key !== key) {
      current = { key, commits: [] };
      groups.push(current);
    }
    current.commits.push(c);
  });
  return groups;
}

const CommitEntry = ({ commit }) => {
  const [expanded, setExpanded] = useState(false);
  const hasBody = (commit.body || '').trim().length > 0;
  const published = (commit.refs || []).filter((r) => r.published_changes);

  return (
    <Segment className="changelog-entry">
      <div>
        <strong>{commit.subject}</strong>
      </div>
      <div style={{ margin: '0.4em 0', color: '#666', fontSize: '0.92em' }}>
        <Icon name="user outline" />
        {commit.author}
        {'  ·  '}
        {new Date(commit.committed_at).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })}
        {'  ·  '}
        <code>{commit.sha.slice(0, 8)}</code>
        {commit.files_changed ? `  ·  ${commit.files_changed} files` : ''}
      </div>
      {(commit.refs || []).length > 0 && (
        <div style={{ margin: '0.4em 0' }}>
          {commit.refs.map((ref) =>
            ref.slug ? (
              <Label
                as={Link}
                to={`/docs/${ref.slug}`}
                key={ref.doc_ref}
                size="small"
                color="blue"
                basic
                title={ref.title || ref.doc_ref}
              >
                {ref.doc_ref}
              </Label>
            ) : (
              <Label key={ref.doc_ref} size="small" basic>
                {ref.doc_ref}
              </Label>
            ),
          )}
        </div>
      )}
      {published.length > 0 && (
        <Message size="tiny" info>
          {published.map((ref) => (
            <p key={ref.doc_ref} style={{ margin: '0.2em 0' }}>
              <Icon name="book" />
              <strong>
                {ref.doc_ref} v{ref.published_version}
              </strong>
              {': '}
              {ref.published_changes.length > 280
                ? `${ref.published_changes.slice(0, 280)}…`
                : ref.published_changes}
            </p>
          ))}
        </Message>
      )}
      {hasBody && (
        <div style={{ marginTop: '0.4em' }}>
          <a
            role="button"
            tabIndex={0}
            onClick={() => setExpanded(!expanded)}
            onKeyPress={() => setExpanded(!expanded)}
            style={{ cursor: 'pointer', fontSize: '0.9em' }}
          >
            <Icon name={expanded ? 'chevron up' : 'chevron down'} />
            {expanded ? 'Hide details' : 'Show details'}
          </a>
          {expanded && (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                background: '#f8f8f8',
                padding: '0.8em',
                marginTop: '0.4em',
                fontSize: '0.88em',
              }}
            >
              {commit.body}
            </pre>
          )}
        </div>
      )}
    </Segment>
  );
};

// DR-2026-236 languages leg: per-language activity ("is it active").
// Two signals rendered separately — last curated work (corrections) vs last
// write (includes bulk operations) — so the active badge stays honest.
const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

const LanguagesView = () => {
  const [languages, setLanguages] = useState([]);
  const [windowDays, setWindowDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/changelog/languages`)
      .then((json) => {
        setLanguages(json.languages || []);
        setWindowDays(json.window_days || 90);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (error) return <Message negative>{error}</Message>;
  if (loading)
    return <Loader active inline="centered" content="Loading languages…" />;

  const activeCount = languages.filter((l) => l.active).length;

  return (
    <>
      <p style={{ color: '#666' }}>
        {activeCount} of {languages.length} languages active in the last{' '}
        {windowDays} days (curated work or any write). Sorted by most recent
        activity.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <Table compact celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Language</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Last curated work</Table.HeaderCell>
              <Table.HeaderCell>Corrections ({windowDays}d)</Table.HeaderCell>
              <Table.HeaderCell>Last write</Table.HeaderCell>
              <Table.HeaderCell>Writes ({windowDays}d)</Table.HeaderCell>
              <Table.HeaderCell>Translations</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {languages.map((l) => (
              <Table.Row key={l.language}>
                <Table.Cell>
                  <Link to={`/polyglot/languages/${l.language}`}>
                    <code>{l.language}</code>{' '}
                    {l.language_name_english || ''}
                    {l.language_name_native &&
                    l.language_name_native !== l.language_name_english
                      ? ` — ${l.language_name_native}`
                      : ''}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  {l.active ? (
                    <Label color="green" size="small">
                      active
                    </Label>
                  ) : (
                    <Label size="small">quiet</Label>
                  )}
                </Table.Cell>
                <Table.Cell>{fmtDate(l.last_correction)}</Table.Cell>
                <Table.Cell textAlign="right">{l.corrections_90d}</Table.Cell>
                <Table.Cell>{fmtDate(l.last_changed)}</Table.Cell>
                <Table.Cell textAlign="right">{l.changed_90d}</Table.Cell>
                <Table.Cell textAlign="right">
                  {l.total_translations}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};

const ChangelogFeed = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [commits, setCommits] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [docType, setDocType] = useState(searchParams.get('doc_type') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchDraft, setSearchDraft] = useState(
    searchParams.get('search') || '',
  );
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState(
    searchParams.get('tab') === 'languages' ? 'languages' : 'commits',
  );

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (docType) params.set('doc_type', docType);
    if (search && search.trim().length >= 3) params.set('search', search.trim());
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@polyglot/changelog?${params.toString()}`)
      .then((json) => {
        setCommits(json.commits || []);
        setPagination(json.pagination || { total: 0, pages: 1 });
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [docType, search, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Mirror filters into the URL (shareable views)
  useEffect(() => {
    const params = new URLSearchParams();
    if (tab === 'languages') params.set('tab', 'languages');
    if (docType) params.set('doc_type', docType);
    if (search) params.set('search', search);
    const qs = params.toString();
    history.replace(`/polyglot/changelog${qs ? `?${qs}` : ''}`);
  }, [tab, docType, search, history]);

  const groups = groupByDay(commits);

  return (
    <Container style={{ padding: '2em 0' }}>
      <Helmet title="Platform Changelog" />
      <Header as="h1">
        <Icon name="history" />
        <Header.Content>
          Platform Changelog
          <Header.Subheader>
            Every change, from the platform&apos;s change-controlled history —
            newest first. Chips link to the governing documents.
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Menu pointing secondary>
        <Menu.Item
          name="Platform changes"
          active={tab === 'commits'}
          onClick={() => setTab('commits')}
        />
        <Menu.Item
          name="Languages"
          active={tab === 'languages'}
          onClick={() => setTab('languages')}
        />
      </Menu>

      {tab === 'languages' ? (
        <LanguagesView />
      ) : (
        <>
          <Segment basic style={{ padding: '0.5em 0' }}>
            <Dropdown
              selection
              options={DOC_TYPE_OPTIONS}
              value={docType}
              placeholder="All document types"
              onChange={(e, { value }) => {
                setDocType(value);
                setPage(1);
              }}
              style={{ marginRight: '1em' }}
            />
            <Input
              icon="search"
              placeholder="Search subjects and messages (min 3 chars)…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setSearch(searchDraft);
                  setPage(1);
                }
              }}
              style={{ minWidth: '22em' }}
            />
          </Segment>

          {error && <Message negative>{error}</Message>}
          {loading ? (
            <Loader active inline="centered" content="Loading changelog…" />
          ) : (
            <>
              <p style={{ color: '#666' }}>
                {pagination.total} change{pagination.total === 1 ? '' : 's'}
              </p>
              {groups.map((group) => (
                <div key={group.key} style={{ marginBottom: '1.5em' }}>
                  <Header as="h3" dividing>
                    {group.key}
                  </Header>
                  {group.commits.map((commit) => (
                    <CommitEntry key={commit.sha} commit={commit} />
                  ))}
                </div>
              ))}
              {pagination.pages > 1 && (
                <Pagination
                  activePage={page}
                  totalPages={pagination.pages}
                  onPageChange={(e, { activePage }) => setPage(activePage)}
                />
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default ChangelogFeed;
